// @flow strict
import type {EmptyStateProps} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';

// $FlowFixMe[nonstrict-import]
import {baseRequest} from 'src/utils/redux-api-v2';
import {duotoneIconStyle} from 'src/utils/app-shell';
// $FlowFixMe[nonstrict-import]
import {isInternalUser} from 'src/utils/check-internal-user';
import classify from 'src/utils/classify';

import {useApiQuery} from 'src/hooks/useApiQuery';
import useI18n from 'src/hooks/useI18n';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {usePageTitle} from 'src/contexts/page-title-context.jsx';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {
  Toast,
  TOAST_SEMANTIC,
  ToastBody,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  SubTitleExtraSmall,
  SubTitleMedium,
  TEXT_COLORS,
  TitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  BasicSingleCell,
  ButtonCta,
  Table,
  TableActionBar,
  TableBottomBar,
  TableTopBar,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';

import css from './missing-labels-container.css';

// Note(Nishant): Not the best way and should have kept as an env variable
// but since we would need this dashboard live early this is the only possible way
// need to refactor this once we get bandwidth
const AUTHED_EMAIL_LIST = ['nishant.gaurav@sensehq.com'];

type LabelEntry = {|
  key: string,
  value: string,
|};

export const TableEmptyState = (): React$Element<
  React$AbstractComponent<EmptyStateProps, HTMLDivElement>,
> => {
  const {t} = useLabelTranslation();

  return (
    <EmptyState
      description={`${t('No_new_labels_found', 'No new labels found')}!`}
      imageVariant="file"
    ></EmptyState>
  );
};

export const MissingLabelsContainer = (): React.Node => {
  const dispatch = useDispatch();
  const {labelI18nInstance} = useI18n();
  const isInternal = isInternalUser();
  const {t, language} = useLabelTranslation();
  const {
    actions: {setPageTitleProps},
  } = usePageTitle();

  const userDetails = useSelector((state) => state.accounts);
  const userEmail = userDetails.data[userDetails?.authedUserId]?.email || '';
  const hasDeleteAccess = AUTHED_EMAIL_LIST.includes(userEmail);

  const [selectedKeys, setSelectedKeys] = React.useState<Array<string>>([]);

  const {
    data: missingLabelsData,
    isLoading,
    refetch,
  } = useApiQuery(
    {pathname: 'dynamic-translation'},
    {
      apiOptions: {
        options: {
          apiPath: '/trmapi/api/',
        },
      },
      queryOptions: {
        select: (data) => {
          const isObject =
            data && typeof data === 'object' && !Array.isArray(data);

          const missingLabelsArray = isObject
            ? Object.entries(data).map(([key, value]) => ({
                key,
                value,
              }))
            : [];
          return {
            map: data,
            array: missingLabelsArray,
          };
        },
      },
    },
  );

  const enJson: {[string]: string} = React.useMemo(() => {
    if (!labelI18nInstance.hasResourceBundle('en', 'translation')) {
      return {};
    }

    const bundle = labelI18nInstance.getResourceBundle('en', 'translation');

    return typeof bundle === 'object' && bundle !== null ? bundle : {};
  }, [missingLabelsData]);

  const transformedEntries: Array<{
    isInEnJson: boolean,
    key: string,
    value: string,
  }> = React.useMemo(() => {
    if (!missingLabelsData?.array) {
      return [];
    }

    return missingLabelsData.array.map(({key, value}) => ({
      key,
      value,
      // $FlowFixMe[method-unbinding]
      isInEnJson: Object.prototype.hasOwnProperty.call(enJson, key),
    }));
  }, [missingLabelsData, enJson]);

  React.useEffect(() => {
    setPageTitleProps({
      customPageNameComponent: (
        <PageName>
          <TitleMedium>
            {t('Missing_Labels_Dashboard', 'Missing Labels Dashboard')}
          </TitleMedium>
          <Icon type="duotone" name="tags" style={duotoneIconStyle} />
        </PageName>
      ),
      showBackButton: false,
    });
  }, [language]);

  const successAction = () => {
    setSelectedKeys([]);
    toastApi.show(
      <Toast initialFocus={0} semantic={TOAST_SEMANTIC.success}>
        <ToastTitle>{t('Delete_Success', 'Delete Success')}</ToastTitle>
        <ToastBody>
          {`${selectedKeys.length} ${t(
            'label_s_are_deleted',
            'label(s) are deleted',
          )}`}
        </ToastBody>
      </Toast>,
    );
    refetch();
  };

  const failureAction = () => {
    setSelectedKeys([]);
    toastApi.show(
      <Toast initialFocus={0} semantic={TOAST_SEMANTIC.danger}>
        <ToastTitle>{t('Delete_Error', 'Delete Error')}</ToastTitle>
        <ToastBody>
          {`${selectedKeys.length} ${t(
            'label_s_are_not_deleted',
            'label(s) are not deleted',
          )}`}
        </ToastBody>
      </Toast>,
    );
    refetch();
  };

  const deleteLabels = async () => {
    if (selectedKeys.length === 0) {
      return;
    }

    // Note(Nishant): Had to use this instead of redux-api-v2 because this api is built differently
    // returns a string in place of an object even when the application/type is set to JSON
    // This should get possibly refactored once the trm team updates this api
    dispatch(
      baseRequest('api/dynamic-translation', {
        method: 'DELETE',
        data: selectedKeys,
        options: {
          apiPath: '/trmapi/',
        },
      }),
    )
      .then(() => {
        successAction();
      })
      .catch((e) => {
        if (e?.response?.ok) {
          successAction();
        } else {
          failureAction();
        }
      });
  };

  function downloadSelectedLabelsAsJson(
    allEntries: Array<LabelEntry>,
    selectedKeys: Array<string>,
    filename: string = 'selected-labels.json',
  ): void {
    const selectedMap: {[string]: string} = allEntries.reduce((acc, entry) => {
      if (selectedKeys.includes(entry.key)) {
        acc[entry.key] = entry.value;
      }
      return acc;
    }, {});

    const blob = new Blob([JSON.stringify(selectedMap, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    const body: HTMLElement | null = document.body;
    if (body != null) {
      body.appendChild(link);
      link.click();
      body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  const selectAllPresentInEnJson = (
    entries: Array<{key: string, isInEnJson: boolean, value: string}>,
  ) => {
    const keys = entries
      .filter((entry) => entry.isInEnJson)
      .map((entry) => entry.key);

    setSelectedKeys(keys);
  };

  const selectAllMissingInEnJson = (
    entries: Array<{key: string, isInEnJson: boolean, value: string}>,
  ) => {
    const keys = entries
      .filter((entry) => !entry.isInEnJson)
      .map((entry) => entry.key);

    setSelectedKeys(keys);
  };

  const renderCell = (data: string) => {
    return (
      <BasicSingleCell>
        <span className={css.jobTitle}>{data}</span>
      </BasicSingleCell>
    );
  };

  const renderIconCell = (isInEnJson: boolean) => {
    return (
      <BasicSingleCell>
        <Icon
          name={isInEnJson ? 'square-check' : 'square-xmark'}
          color={isInEnJson ? TEXT_COLORS.success : TEXT_COLORS.danger}
        ></Icon>
      </BasicSingleCell>
    );
  };

  const TABLE_HEADERS = [
    {
      label: t('Key_Name', 'Key Name'),
      key: 'key',
      sortable: true,
      className: css.tableColumn,
      render: ({data}) => renderCell(data.key),
    },
    {
      label: t('Label_Value', 'Label Value'),
      key: 'value',
      sortable: true,
      className: css.tableColumn,
      render: ({data}) => renderCell(data.value),
    },
    {
      label: t('Present_in_enjson', 'Present in en.json'),
      key: 'isInEnJson',
      sortable: false,
      className: css.shortTableColumn,
      render: ({data}) => renderIconCell(data.isInEnJson),
    },
  ];

  if (!isInternal) {
    return (
      <EmptyState
        description={`${t(
          'You_dont_have_permission_to_view_this_page',
          'You don’t have permission to view this page',
        )}`}
        imageVariant="file"
      />
    );
  }

  return (
    <div className={css.container}>
      <TableTopBar>
        <div className={css.leftActionSlot}>
          <SubTitleMedium>{t('Source_Data', 'Source Data')}</SubTitleMedium>
        </div>
        <div className={css.rightActionSlot}>
          <Tooltip
            body={t(
              'Select_All_Labels_Missing_In_enjson',
              'Select All Labels Missing In en.json',
            )}
          >
            <Button
              iconLeftName="cloud-exclamation"
              type="ghost"
              size="small"
              ariaLabel={t(
                'Select_All_Labels_Missing_In_enjson',
                'Select All Labels Missing In en.json',
              )}
              onClick={() => selectAllMissingInEnJson(transformedEntries)}
            ></Button>
          </Tooltip>
          <Tooltip
            body={t(
              'Select_All_Labels_Present_In_enjson',
              'Select All Labels Present In en.json',
            )}
          >
            <Button
              iconLeftName="cloud-check"
              type="ghost"
              size="small"
              ariaLabel={t(
                'Select_All_Labels_Present_In_enjson',
                'Select All Labels Present In en.json',
              )}
              onClick={() => selectAllPresentInEnJson(transformedEntries)}
            ></Button>
          </Tooltip>
        </div>
      </TableTopBar>
      <Table
        idName="key"
        borderRadius="0"
        classNames={{
          wrapper: classify(css.tableWrapper, {
            [css.withActionBar]: selectedKeys.length > 0,
          }),
        }}
        entries={transformedEntries || []}
        headers={TABLE_HEADERS}
        stickyHeader
        isLoading={isLoading}
        emptyText={<TableEmptyState />}
        selectedKeys={selectedKeys}
        defaultSortDirection="asc"
        defaultSortKey="key"
        onSelect={(keys) => {
          setSelectedKeys(keys);
        }}
      />
      {selectedKeys.length > 0 && (
        <TableActionBar className={css.actionBar}>
          <div className={css.leftActionSlot}></div>
          <div className={css.middleActionSlot}>
            <ButtonCta
              size="small"
              iconLeftName="cloud-arrow-down"
              onClick={() => {
                if (
                  missingLabelsData?.array &&
                  Array.isArray(missingLabelsData.array)
                ) {
                  downloadSelectedLabelsAsJson(
                    missingLabelsData.array,
                    selectedKeys,
                  );
                }
              }}
            >
              {t('Download_Labels', 'Download Labels')}
            </ButtonCta>
            {hasDeleteAccess && (
              <ButtonCta
                size="small"
                iconLeftName="trash-can"
                onClick={deleteLabels}
              >
                {t('Delete_Labels', 'Delete Labels')}
              </ButtonCta>
            )}
          </div>
          <div className={css.rightActionSlot}>
            <ButtonCta
              size="small"
              iconLeftName="close"
              onClick={() => {
                setSelectedKeys([]);
              }}
            ></ButtonCta>
          </div>
        </TableActionBar>
      )}

      <TableBottomBar>
        <SubTitleExtraSmall color="secondary">
          {`${t('Labels_Selected', 'Labels Selected')}: `}
          {selectedKeys.length || 0}
        </SubTitleExtraSmall>
        <SubTitleExtraSmall color="secondary">
          {`${t('Total_Labels', 'Total Labels')}: `}
          {transformedEntries.length || 0}
        </SubTitleExtraSmall>
      </TableBottomBar>
    </div>
  );
};

export default MissingLabelsContainer;
